/* MD · Sticky-add-to-cart bridge — v4
   Horizon's native <sticky-add-to-cart> requires a <product-form-component>
   inside a .buy-buttons-block to set up its IntersectionObserver. MD does
   not render those (we replaced buy-buttons with custom [data-md-fop-add] /
   [data-bf-checkout] / [data-md-simple-add] CTAs in md-pdp-custom.liquid).
   Result: the native observer bails on setup, data-stuck stays "false"
   permanently, and the sticky bar is never visible.

   This bridge owns four behaviors:
   1. Visibility — observe the visible custom CTA. When it scrolls above
      viewport, set sticky bar data-stuck="true". When it's back in view
      or the footer enters viewport, set "false".
   2. Title — combine product title + selected variant into a single
      "Producto · Variante" label inside .sticky-add-to-cart__title.
      Hide the secondary .sticky-add-to-cart__variant line.
   3. Image — swap to the selected variant's featured_image.
   4. Click — intercept in capture phase on the host element and delegate
      to the custom add button (which already handles cart.js + drawer open).
      Respects the server-side disabled state (does not force-enable).
*/
(function () {
  'use strict';

  /* ─── 1. Resolve current variant from any custom selector on the page ─── */

  function findCurrentVariant() {
    /* md-simple (simple products with variants — antifaz, scrunchies, etc.) */
    const simple = document.querySelector('[data-md-simple]');
    if (simple) {
      const cfgEl = simple.querySelector('script[data-md-simple-config]');
      if (cfgEl) {
        try {
          const cfg = JSON.parse(cfgEl.textContent || '{}');
          const opts = cfg.options || [];
          const variants = cfg.variants || [];
          const sel = {};
          opts.forEach((o) => {
            const swatch = simple.querySelector(`[data-md-simple-swatch][data-option-position="${o.position}"].is-selected`);
            const select = simple.querySelector(`[data-md-simple-select][data-option-position="${o.position}"]`);
            if (swatch) sel[o.position] = swatch.dataset.value;
            else if (select) sel[o.position] = select.value;
            else sel[o.position] = o.selected_value;
          });
          const v = variants.find((vv) => opts.every((o) => {
            const s = sel[o.position];
            if (o.position === 1) return vv.option1 === s;
            if (o.position === 2) return vv.option2 === s;
            if (o.position === 3) return vv.option3 === s;
            return true;
          })) || variants.find((vv) => vv.id === cfg.default_variant_id);
          if (v) return { variant: v, productHandle: cfg.product_handle };
        } catch (e) { /* ignore */ }
      }
    }

    /* md-fop (gift section — antifaz/funda/turbante + regalo) */
    const fop = document.querySelector('[data-md-fop]');
    if (fop) {
      const cfgEl = fop.querySelector('script[data-md-fop-config]');
      if (cfgEl) {
        try {
          const cfg = JSON.parse(cfgEl.textContent || '{}');
          const variants = cfg.variants || [];
          const opts = cfg.options || [];
          const selects = fop.querySelectorAll('[data-md-fop-option-name]');
          const sel = {};
          opts.forEach((o) => { sel[o.position] = o.selected_value; });
          selects.forEach((s) => {
            const pos = parseInt(s.dataset.mdFopOptionPosition || '0', 10);
            if (pos) sel[pos] = s.value;
          });
          const v = variants.find((vv) => opts.every((o) => {
            const sv = sel[o.position];
            if (o.position === 1) return vv.option1 === sv;
            if (o.position === 2) return vv.option2 === sv;
            if (o.position === 3) return vv.option3 === sv;
            return true;
          })) || variants.find((vv) => vv.id === cfg.default_variant_id);
          if (v) return { variant: v, productHandle: cfg.funda_handle };
        } catch (e) { /* ignore */ }
      }
    }

    return null;
  }

  /* ─── 2. Build "Producto · Variante" label + update image ─── */

  function getBaseProductTitle(sticky) {
    /* Use the H1 of the PDP if available; fall back to the title that the
       server rendered into the sticky (which is product.title). */
    const cached = sticky.dataset.mdBaseTitle;
    if (cached) return cached;
    const h1 = document.querySelector('h1');
    let title = (h1 && h1.textContent || '').trim();
    if (!title) {
      const t = sticky.querySelector('.sticky-add-to-cart__title');
      title = (t && t.textContent || '').trim();
    }
    sticky.dataset.mdBaseTitle = title;
    return title;
  }

  function updateStickyVisuals() {
    const sticky = document.querySelector('sticky-add-to-cart');
    if (!sticky) return;
    const baseTitle = getBaseProductTitle(sticky);
    const state = findCurrentVariant();

    /* Variant parts (skip "Default Title" placeholder) */
    let variantLabel = '';
    if (state && state.variant) {
      const v = state.variant;
      const parts = [];
      if (v.option1 && v.option1 !== 'Default Title') parts.push(v.option1);
      if (v.option2) parts.push(v.option2);
      if (v.option3) parts.push(v.option3);
      variantLabel = parts.join(' / ');
    }

    /* Combined title in .sticky-add-to-cart__title */
    const titleEl = sticky.querySelector('.sticky-add-to-cart__title');
    if (titleEl) {
      titleEl.textContent = variantLabel ? `${baseTitle} · ${variantLabel}` : baseTitle;
    }
    /* Hide the secondary line — we already include variant in the title */
    const variantEl = sticky.querySelector('.sticky-add-to-cart__variant');
    if (variantEl) variantEl.style.display = 'none';

    /* Image: swap to variant.featured_image.src */
    if (state && state.variant && state.variant.featured_image && state.variant.featured_image.src) {
      const img = sticky.querySelector('.sticky-add-to-cart__image-img, [ref="productImage"]');
      if (img) {
        let src = state.variant.featured_image.src;
        if (src.startsWith('//')) src = 'https:' + src;
        const noQuery = src.split('?')[0];
        const ver = (src.match(/v=(\d+)/) || [])[1];
        const qs = ver ? `?v=${ver}&width=240` : '?width=240';
        img.src = `${noQuery}${qs}`;
        img.removeAttribute('srcset');
      }
    }

    /* Sync data attr (some Horizon consumers read it) */
    if (state && state.variant && state.variant.id) {
      sticky.setAttribute('data-current-variant-id', String(state.variant.id));
    }
  }

  /* ─── 3. Click delegation — capture phase, no DOM mutation ─── */

  function findCustomAddBtn() {
    return document.querySelector('[data-md-fop-add]:not([disabled])')
        || document.querySelector('[data-bf-checkout]:not([disabled])')
        || document.querySelector('[data-md-simple-add]:not([disabled])')
        || document.querySelector('[data-md-fop-add]')
        || document.querySelector('[data-bf-checkout]')
        || document.querySelector('[data-md-simple-add]');
  }

  function wireClickDelegation(sticky) {
    if (sticky.dataset.mdBridgeClick === '1') return;
    sticky.dataset.mdBridgeClick = '1';

    /* Capture phase on host: runs BEFORE Horizon's on:click handler on the
       button, so stopImmediatePropagation neutralizes the native flow that
       targets a non-existent product-form-component. */
    sticky.addEventListener('click', function (e) {
      const btn = e.target.closest('[ref="addToCartButton"], .sticky-add-to-cart__button');
      if (!btn) return;
      /* Respect server-side disabled (bundles without inventory tracking) */
      if (btn.disabled || btn.hasAttribute('disabled')) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const target = findCustomAddBtn();
      if (!target) {
        console.warn('[md-sticky-bridge] no custom add button found');
        return;
      }

      const txt = btn.querySelector('.add-to-cart-text__content span:first-child');
      const orig = txt ? txt.textContent : '';
      btn.disabled = true;
      if (txt) txt.textContent = 'Agregando…';
      target.click();
      setTimeout(() => {
        btn.disabled = false;
        if (txt) txt.textContent = orig;
      }, 1500);
    }, true);
  }

  /* ─── 4. Visibility observer — owns data-stuck ─── */

  function wireVisibility(sticky) {
    if (sticky.dataset.mdBridgeObs === '1') return;
    const stickyBar = sticky.querySelector('[ref="stickyBar"], .sticky-add-to-cart__bar');
    const target = findCustomAddBtn();
    if (!stickyBar || !target) return;
    sticky.dataset.mdBridgeObs = '1';

    let nearFooter = false;
    let pastCta = false;

    function reflect() {
      stickyBar.dataset.stuck = (pastCta && !nearFooter) ? 'true' : 'false';
    }

    const ctaObs = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;
      if (!entry.isIntersecting) {
        const rect = entry.target.getBoundingClientRect();
        /* Only show if CTA is ABOVE the viewport (scrolled past), not below */
        pastCta = rect.bottom < 0;
      } else {
        pastCta = false;
      }
      reflect();
    });
    ctaObs.observe(target);

    const footer = document.querySelector('footer') || document.querySelector('[class*="footer-group"]');
    if (footer) {
      const footerObs = new IntersectionObserver((entries) => {
        const [entry] = entries;
        if (!entry) return;
        nearFooter = entry.isIntersecting;
        reflect();
      }, { rootMargin: '200px 0px 0px 0px' });
      footerObs.observe(footer);
    }
  }

  /* ─── Init ─── */

  function applyAll() {
    document.querySelectorAll('sticky-add-to-cart').forEach((sticky) => {
      wireClickDelegation(sticky);
      wireVisibility(sticky);
    });
    updateStickyVisuals();
  }

  function bindVariantWatchers() {
    document.addEventListener('click', (e) => {
      if (e.target.closest && e.target.closest('[data-md-simple-swatch]')) {
        setTimeout(updateStickyVisuals, 0);
      }
    }, true);
    document.addEventListener('change', (e) => {
      const t = e.target;
      if (t && t.matches && t.matches('[data-md-simple-select], [data-md-fop-option-name], .md-fop__option-select')) {
        setTimeout(updateStickyVisuals, 0);
      }
    }, true);
  }

  function init() {
    applyAll();
    bindVariantWatchers();
    /* Re-attempt: customElements.whenDefined for sticky-add-to-cart and
       md-pdp-simple.js / md-funda-with-options.js may load after this script. */
    let attempts = 0;
    const iv = setInterval(() => {
      attempts++;
      applyAll();
      if (attempts >= 6) clearInterval(iv);
    }, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
